import { MarkdownPreview } from '@shared/markdown';
import { Anchor, Box, NameValueList, NameValuePair, Text } from 'grommet';
import { Share } from 'grommet-icons';
import React from 'react';
import styled from 'styled-components';

import { Dialog, DialogBody, DialogHeader } from '@/components/dialog';
import Nullish from '@/components/nullish';
import CoverageTagChip from '@/features/coverage/components/coverage-tag-chip';
import { CoverageTag } from '@/features/drugs/types/coverage-tag';
import { Lookup } from '@/types/lookup';
import { State } from '@/types/state';
import { stepTherapyDisplay } from '@/utils/state-coverage';

type PaCriteriaDialogProps = {
  open: boolean;
  onClose: () => void;
  drugName?: string;
  therapeuticClass?: Lookup;
  state?: State;
  coverageTags?: CoverageTag[];
  paTypes?: Lookup[];
  stepTherapyCount?: number;
  hasAdditionalSteps?: boolean;
  paCriteriaDescription?: string;
  paCriteriaSourceUrl?: string;
};

export const PaCriteriaDialog = (props: PaCriteriaDialogProps) => {
  const {
    open,
    onClose,
    drugName,
    therapeuticClass,
    state,
    coverageTags,
    paTypes,
    stepTherapyCount,
    hasAdditionalSteps,
    paCriteriaDescription,
    paCriteriaSourceUrl
  } = props;

  const hasContext = drugName || therapeuticClass || state || coverageTags?.length;

  return (
    <Dialog open={open}>
      <DialogHeader title="PA Criteria" onClose={onClose}/>
      <DialogBody>
        <Box gap="small">
          {hasContext && (
            <NameValueList>
              {state && (
                <NameValuePair name="State"><Text>{state.name}</Text></NameValuePair>
              )}
              {drugName && (
                <NameValuePair name="Drug"><Text>{drugName}</Text></NameValuePair>
              )}
              {therapeuticClass && (
                <NameValuePair name="Classification"><Text>{therapeuticClass.label}</Text></NameValuePair>
              )}
              {coverageTags?.length ? (
                <NameValuePair name="Coverage Tags">
                  <Box direction="row" wrap gap="xsmall">
                    {coverageTags.map(tag => <CoverageTagChip key={tag.id} coverageTag={tag}/>)}
                  </Box>
                </NameValuePair>
              ) : null}
            </NameValueList>
          )}
          <NameValueList>
            <NameValuePair name="PA Types">
              <Text>{paTypes?.map(it => it.label)?.join('; ') ?? <>&mdash;</>}</Text>
            </NameValuePair>
            <NameValuePair name="Step Therapy">
              <Text>
                <Nullish value={stepTherapyDisplay(stepTherapyCount, hasAdditionalSteps)}/>
              </Text>
            </NameValuePair>
          </NameValueList>
          <MarkdownPreview markdown={paCriteriaDescription}/>
          {paCriteriaSourceUrl ? (
            <span>
              <SourceLink href={paCriteriaSourceUrl} target="_blank" referrerPolicy="no-referrer">
                View Source
                <Share color="contrast-1"/>
              </SourceLink>
            </span>
          ) : null}
        </Box>
      </DialogBody>
    </Dialog>
  );
};

const SourceLink = styled(Anchor)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: max-content;
`;
